/**
 * Sectioned nav model (DNKADM-0285, Phase 2a of the Concept-B nav rebuild —
 * `docs/design/dink-admin-nav-rebuild-plan.md`).
 *
 * Replaces the old flat `OPERATIONS`/`CONFIGURATION` arrays with two
 * **sections**: **Run** (daily work, always visible) and **Set up** (hidden
 * entirely in Front Desk view — same gate as the old Configuration group,
 * just retargeted). Each section holds **items** (a single link) and
 * **groups** (a labelled, collapsible parent of items). Every leaf keeps the
 * exact route it had before this spec — this is a presentational regroup
 * only, no route changes.
 *
 * Round 2 (post-open PR #457 review): a leaf's **section is IA, not
 * authority**. Whether a leaf shows the locked affordance, or is hidden in
 * Front Desk view, follows its own `gated` flag — a direct mirror of whether
 * `src/router.tsx` wraps that path in `RequireAdmin` — not which section it
 * happens to sit in. Concept B deliberately mixes gated and ungated leaves
 * within a section (Run > History has both Activity (ungated) and Config
 * changes (gated)), so the two can no longer be derived from each other.
 *
 * Round 3 (Architect/Operator ruling): Policies (the ungated, read-only
 * operator policy reference) moved from Set up > Settings into Run — it's a
 * daily reference front-desk staff could already reach pre-0285, not admin
 * setup. Cancellation policy (the actual, still-gated editor) stays in
 * Set up > Settings.
 *
 * DNKADM-0302: Payments (the Square-connection screen + its status dashboard
 * — setup acts, not daily work) moved from Run > Billing into Set up, and the
 * Billing group dissolved: a labelled disclosure group holding exactly one
 * link (Transaction ledger) is dead weight expanded and an extra tap in the
 * collapsed rail, so the ledger is now a flat Run item. Route paths and
 * `RequireAdmin` gating are unchanged — `/settings/payments` was already
 * admin-gated, which now also matches its section.
 *
 * DNKADM-0303 (Operator ruling, 2026-08-14): Payments nests INSIDE the
 * Settings group — it belongs alongside the other once-per-tenant
 * configuration, not flat between Memberships and Settings. A placement
 * refinement of 0302, not a reversal; route and gating still unchanged.
 *
 * DNKADM-0359 (the customer/staff split) gave each leaf a per-leaf `staffVisible`
 * flag, because the STAFF floor was read-only and some UNGATED surfaces (the
 * Transaction ledger, Activity) `403`d for it. DNKADM-0375 (Operator ruling
 * 2026-09-05, DNKAPI-0532): STAFF is front of house — every RUN capability, no
 * SET UP capability. dink-api now opens every ungated surface to STAFF, so the
 * per-leaf flag is gone and ONE predicate decides, {@link staffSeesLeaf}: a STAFF
 * session sees exactly the leaves that are not `gated`, and the rest HIDDEN, not
 * locked. A rule stated once cannot half-apply.
 *
 * DNKADM-0363: **My schedule** joins Run, directly under Reservations — a
 * linked staff member's own hours, locations, date exceptions and bookings.
 * It brings the router-truth flag `selfScoped`: the leaf's subject is
 * the signed-in operator's OWN staff record, which a platform-support session
 * (no roster row, by construction) does not have, so the shell hides it there.
 * It is presentation — the route is ungated and the screen renders whatever
 * `GET /v2/me/staff` answers.
 */

export interface NavLeaf {
  kind: 'item';
  /** Material Symbols Outlined glyph name. */
  icon: string;
  label: string;
  path: string;
  /**
   * True iff this path is wrapped in `RequireAdmin` in `src/router.tsx` —
   * the router's own ground truth, not a nav-authored guess. A gated leaf
   * shows the locked affordance to a non-admin and is hidden entirely in
   * Front Desk view; an ungated leaf shows neither, regardless of which
   * section or group it's presented in. Covered by the drift guard in
   * `navModel.test.ts`.
   */
  gated: boolean;
  /**
   * True iff this destination's primary content is scoped to the SELECTED
   * service location — it cannot render its own subject without one
   * (DNKADM-0359).
   *
   * Today that is the reservations board alone: `locationId` is a REQUIRED
   * param of `listReservations` and the fetcher returns `null` without one
   * (`ReservationsCalendar` — "resolve a location BEFORE the first fetch"), so
   * the screen is one location's day or nothing. Every other destination either
   * sends `locationId` only when a location happens to be selected and renders
   * tenant-wide otherwise (Transaction ledger, Activity), or never reads the
   * app-level selection at all (Set up keeps its own per-screen selector —
   * the Operations-only scope rule, DNKADM-0033 U2).
   *
   * Consumed ONLY by the shell's no-assignment empty state: a STAFF operator
   * with no locations assigned gets that state on these destinations, and their
   * UNSCOPED ones (Policies, which reads a public tenant-wide bundle) keep
   * working. It is presentation, not authorization — it mirrors nothing on the
   * server and denies nothing. Covered by the drift guard in `navModel.test.ts`.
   */
  locationScoped: boolean;
  /**
   * True iff this destination's subject is the signed-in operator's OWN staff
   * record (DNKADM-0363) — My schedule, and nothing else today.
   *
   * A destination like that has no subject for a session that HAS no staff
   * record. A platform-support session is exactly that case: it is a platform
   * SUPER_ADMIN acting inside one tenant with no roster row by construction
   * (DNKADM-0356), so dink-api answers its `GET /v2/me/staff` with `403
   * NOT_TENANT_STAFF`. The shell therefore hides these leaves from a support
   * session — hidden, not locked, and for the same reason as every other hide
   * here: the door leads nowhere for them.
   *
   * Presentation only, like the flags above. It authorizes nothing: the route
   * stays reachable by URL and the screen renders the server's own answer
   * (`403` → the forbidden state), so this flag can never be the thing that
   * decides. Covered by the drift guard in `navModel.test.ts`.
   */
  selfScoped: boolean;
}

export interface NavGroup {
  kind: 'group';
  /** Stable id used for expand/collapse persistence and active-group lookup. */
  id: string;
  icon: string;
  label: string;
  /**
   * Initial open state for a group the user hasn't toggled yet. Run groups
   * start open (daily work, front-and-center); Set up groups start closed
   * (tucked away) — see the target nav model in the DNKADM-0285 spec.
   */
  defaultExpanded: boolean;
  children: NavLeaf[];
}

type NavEntry = NavLeaf | NavGroup;

export interface NavSection {
  id: 'run' | 'setup';
  label: string;
  entries: NavEntry[];
}

export const RUN_SECTION: NavSection = {
  id: 'run',
  label: 'Run',
  entries: [
    // DNKADM-0345: the surface is called Users — it always read the tenant-wide
    // login roster (`GET /v2/admin/users`), staff included, not a members-only
    // list; only the nav label lagged that reality. The route STAYS `/members`
    // (changing it would break saved links/bookmarks for no user-visible gain).
    //
    // DNKADM-0375: a STAFF session gets the same Users roster an admin does —
    // dink-api opens `GET /v2/admin/users` and its invitations to STAFF (DNKAPI-0532).
    {
      kind: 'item',
      icon: 'group',
      label: 'Users',
      path: '/members',
      gated: false,
      locationScoped: false,
      selfScoped: false,
    },
    // Confirmations folded into Reservations as the Pending tab (DNKADM-0287,
    // phase 2b — the interim standalone 2a item is retired); the pending-count
    // badge now rides this leaf. `/confirmations` redirects to
    // `/reservations/pending` (router.tsx).
    {
      kind: 'item',
      icon: 'event',
      label: 'Reservations',
      path: '/reservations',
      gated: false,
      locationScoped: true,
      selfScoped: false,
    },
    // My schedule (DNKADM-0363) — a linked staff member's own week: their hours,
    // their locations, their date exceptions, their bookings. It sits directly
    // under Reservations because it is the same daily work seen from the other
    // side: the board is the location's day, this is yours.
    //
    // UNGATED, like its neighbour, so a STAFF session sees it. The implemented rule is that the leaf shows to
    // EVERY session except a platform-support one — `selfScoped` is the only hide
    // (a support principal holds no roster row, so it has no "my" schedule;
    // DNKADM-0356). It is deliberately NOT keyed on `principal_type`/
    // `isTenantStaff`: that claim is informational only (`authContextValue.ts`
    // says never gate on it), and many OWNER tokens carry no `principal_type` at
    // all, so keying on it would take the leaf away from exactly those sessions.
    // Nothing is lost by showing it broadly — the SERVER distinguishes linked
    // (`200`) from unlinked (`404 STAFF_NOT_LINKED`), and an admin who does not
    // coach simply gets the empty state.
    //
    // NOT `locationScoped`: the screen resolves its own locations from the coach
    // record's assignments, so it never reads the app-level location selection
    // and must not be swapped for the shell's no-assignment empty state — that
    // state now LINKS here.
    {
      kind: 'item',
      icon: 'calendar_month',
      label: 'My schedule',
      path: '/my-schedule',
      gated: false,
      locationScoped: false,
      selfScoped: true,
    },
    // The former Billing group's sole remaining child, flat since its
    // group-mate Payments moved to Set up (DNKADM-0302 — see header note).
    {
      kind: 'item',
      icon: 'account_balance',
      label: 'Transaction ledger',
      path: '/transaction-ledger',
      gated: false,
      locationScoped: false,
      selfScoped: false,
    },
    // ⚠️ POLICIES STAYS IN RUN, UNGATED. Do not move it to Set up — this has
    // now been proposed and rejected THREE times (DNKADM-0285 rounds 1/2,
    // the round-3 Architect/Operator ruling, and again pre-DNKADM-0302).
    // Policies is the ungated, read-only operator policy reference
    // (PolicyReferenceScreen) — front-desk staff could reach it before
    // DNKADM-0285, and moving it into the admin-gated Set up section takes
    // it away from them (Set up is hidden entirely in Front Desk view and
    // its natural gating is RequireAdmin). The actual editor, Cancellation
    // policy (PoliciesScreen), lives in Set up > Settings and is gated.
    // `navModel.test.ts` asserts this placement explicitly.
    // DNKADM-0303 (Operator): Policies sits BELOW the ledger — the other Run
    // items are things you DO; Policies is something you READ, so it closes
    // the flat run rather than interrupting it. Same section, same gating.
    {
      kind: 'item',
      icon: 'policy',
      label: 'Policies',
      path: '/policies',
      gated: false,
      locationScoped: false,
      selfScoped: false,
    },
    {
      kind: 'group',
      id: 'history',
      icon: 'history',
      label: 'History',
      defaultExpanded: true,
      children: [
        // `timeline` (event sequence), not `receipt_long` (DNKADM-0302 icon
        // audit): a receipt misreads as billing next to Transaction ledger.
        {
          kind: 'item',
          icon: 'timeline',
          label: 'Activity',
          path: '/activity',
          gated: false,
          locationScoped: false,
          selfScoped: false,
        },
        // RequireAdmin-gated despite sitting in Run — same round-2 note as
        // Payments above.
        {
          kind: 'item',
          icon: 'manage_history',
          label: 'Config changes',
          path: '/config-audit',
          gated: true,
          locationScoped: false,
          selfScoped: false,
        },
      ],
    },
  ],
};

export const SETUP_SECTION: NavSection = {
  id: 'setup',
  label: 'Set up',
  entries: [
    {
      kind: 'group',
      // `inventory_2` (boxed goods), not `category` (DNKADM-0302 icon audit):
      // the abstract-shapes glyph was confusable with Services'
      // `design_services` drafting tools in the label-less collapsed rail.
      id: 'catalog',
      icon: 'inventory_2',
      label: 'Catalog',
      defaultExpanded: false,
      children: [
        {
          kind: 'item',
          icon: 'design_services',
          label: 'Services',
          path: '/services',
          gated: true,
          locationScoped: false,
          selfScoped: false,
        },
        // `badge` (employee ID badge), not `groups` (DNKADM-0302 icon audit):
        // `groups` was near-identical to Members' `group`. `badge` was freed
        // by Member profile fields below.
        {
          kind: 'item',
          icon: 'badge',
          label: 'Staff',
          path: '/staff',
          gated: true,
          locationScoped: false,
          selfScoped: false,
        },
        {
          kind: 'item',
          icon: 'meeting_room',
          label: 'Resources',
          path: '/resources',
          gated: true,
          locationScoped: false,
          selfScoped: false,
        },
      ],
    },
    {
      kind: 'item',
      icon: 'workspace_premium',
      label: 'Memberships',
      path: '/membership-credits',
      gated: true,
      locationScoped: false,
      selfScoped: false,
    },
    {
      kind: 'group',
      id: 'settings',
      icon: 'tune',
      label: 'Settings',
      defaultExpanded: false,
      children: [
        // Moved from Run > Billing in DNKADM-0302, then nested here in
        // DNKADM-0303 (Operator ruling): the whole destination — connection
        // editor, payment methods, AND the status-only Overview tab — is
        // once-per-tenant configuration like its group-mates. First child so
        // the Square-connection entry point stays as discoverable as a nested
        // item can be. Already RequireAdmin-gated in router.tsx.
        {
          kind: 'item',
          icon: 'credit_card',
          label: 'Payments',
          path: '/settings/payments',
          gated: true,
          locationScoped: false,
          selfScoped: false,
        },
        {
          kind: 'item',
          icon: 'apartment',
          label: 'Locations',
          path: '/locations',
          gated: true,
          locationScoped: false,
          selfScoped: false,
        },
        {
          kind: 'item',
          // `dynamic_form` (form fields — the more accurate glyph anyway),
          // freeing `badge` for Staff (DNKADM-0302 icon audit).
          icon: 'dynamic_form',
          label: 'Member profile fields',
          path: '/member-profile-fields',
          gated: true,
          locationScoped: false,
          selfScoped: false,
        },
        {
          kind: 'item',
          icon: 'gavel',
          label: 'Cancellation policy',
          path: '/cancellation-policy',
          gated: true,
          locationScoped: false,
          selfScoped: false,
        },
        {
          kind: 'item',
          icon: 'language',
          label: 'Tenant settings',
          path: '/tenant-settings',
          gated: true,
          locationScoped: false,
          selfScoped: false,
        },
        {
          kind: 'item',
          icon: 'admin_panel_settings',
          label: 'Team & access',
          path: '/platform-members',
          gated: true,
          locationScoped: false,
          selfScoped: false,
        },
        {
          kind: 'item',
          icon: 'settings',
          label: 'General',
          path: '/settings',
          gated: true,
          locationScoped: false,
          selfScoped: false,
        },
      ],
    },
  ],
};

const NAV_SECTIONS: readonly NavSection[] = [RUN_SECTION, SETUP_SECTION];

/** Every leaf across both sections, in document order — the flat form the router-drift guard checks. */
export const ALL_NAV_LEAVES: readonly NavLeaf[] = NAV_SECTIONS.flatMap((section) =>
  section.entries.flatMap((entry) => (entry.kind === 'item' ? [entry] : entry.children)),
);

/** Every nav destination path across both sections, in document order. */
export const ALL_NAV_PATHS: readonly string[] = ALL_NAV_LEAVES.map((leaf) => leaf.path);

/**
 * Whether a STAFF session sees this leaf (DNKADM-0375) — the ONE rule, stated once:
 * every RUN capability, no SET UP capability (Operator ruling 2026-09-05). RUN is
 * whatever `RequireAdmin` does not wrap, so the rule is `!gated`, read off the same
 * router-truth flag the drift guard pins. Config changes sits in Run but is gated
 * (dink-api's config feed is ADMIN only), so it is hidden by this same rule.
 * Presentation only: authorization stays the server's.
 */
export function staffSeesLeaf(leaf: NavLeaf): boolean {
  return !leaf.gated;
}

/**
 * Every nav destination whose primary content is scoped to the selected service
 * location (DNKADM-0359) — derived from the leaves themselves, so the shell's
 * no-assignment empty state is decided by the nav model rather than by matching
 * URLs against a hand-kept list. See {@link NavLeaf.locationScoped}.
 */
export const LOCATION_SCOPED_NAV_PATHS: readonly string[] = ALL_NAV_LEAVES.filter(
  (leaf) => leaf.locationScoped,
).map((leaf) => leaf.path);

/**
 * Resolve the single active nav path for a pathname using longest-prefix-wins
 * (DNKADM-0115). A nav leaf matches when the pathname equals its path or sits
 * beneath it at a `/` boundary (e.g. `/members/:id` -> `/members`). When
 * several leaves match, the most specific (longest) match wins, so a child
 * route highlights only its own leaf and not a parent. Returns null when
 * nothing matches.
 */
export function activeNavPath(
  pathname: string,
  paths: readonly string[] = ALL_NAV_PATHS,
): string | null {
  let best: string | null = null;
  for (const path of paths) {
    const matches = pathname === path || pathname.startsWith(`${path}/`);
    if (matches && (best === null || path.length > best.length)) {
      best = path;
    }
  }
  return best;
}

/**
 * What a role can reach, derived from the SAME leaves that render the menu
 * (DNKADM-0302 U4 — the Settings → General role card). Deliberately not a
 * hand-written permissions list: `gated` mirrors `RequireAdmin` in
 * `src/router.tsx` (enforced by the drift guard in `navModel.test.ts`), so
 * this summary cannot drift from actual gating the way prose would.
 */
export function navAccessSummary(isAdmin: boolean): {
  /** Labels of nav destinations this role can open, in nav order. */
  reachable: string[];
  /** Labels locked behind Owner/Admin for this role (empty for admins). */
  adminOnly: string[];
} {
  const reachable: string[] = [];
  const adminOnly: string[] = [];
  for (const leaf of ALL_NAV_LEAVES) {
    (leaf.gated && !isAdmin ? adminOnly : reachable).push(leaf.label);
  }
  return { reachable, adminOnly };
}

/** The id of the group (if any) whose children contain the given active path. */
export function activeGroupId(activePath: string | null): string | null {
  if (activePath === null) return null;
  for (const section of NAV_SECTIONS) {
    for (const entry of section.entries) {
      if (entry.kind === 'group' && entry.children.some((child) => child.path === activePath)) {
        return entry.id;
      }
    }
  }
  return null;
}
